// controller-redirect-decision-ticket.ts
//
// "Is this decision ticket one the CONTROLLER wrote?" — one answer shared by
// G2 (D5: such tickets are not plan content and may run while the plan is not
// ready) and phase 06e R3 (only such a ticket may supersede a blocked plan).
// Two writers: a `redirect --text` (redirect-text-to-decision-ticket.ts) and,
// since 3.40.0, orchestrator recovery (recovery-ticket-materializer.ts, D2.6).
//
// `kind`/`sources` prove nothing on their own: a worker can `create` a
// decision ticket with any `sources` and `amend` them. What only the
// controller writes is the ledger in the journal — an intent naming
// `params.ticket_id` whose result is `applied` (a `failed` recovery try is
// never recognised) — and the ticket must still have the read-only shape it
// was written with (never amended: revision 1). Requiring `applied` for the
// redirect is stricter on purpose and changes nothing: the redirect journals
// intent + result in one commit.
//
// Security audit H-1: shape + label can be forged by a worker that plants a
// second file with the same id. Each op also pins the content it wrote
// (`ticket_sha256` on its `applied` result, ticket-content-sha256.ts); a ticket whose content
// differs — or an op without the hash — is never controller-authored. The hash
// sits on the `applied` RESULT, written after the commit: a resumed recovery
// may rebuild a different brief than the one its intent was journaled for.
import type { MissionState } from "./mission-state-reducer.js";
import { RECOVERY_TICKET_OP_KIND } from "./recovery-guards.js";
import { RECOVERY_TICKET_TRIGGERS, type RecoveryTicketTrigger } from "./recovery-ticket-shape.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { ticketContentSha256 } from "./ticket-content-sha256.js";

const REDIRECT_DECISION_OPERATION_KIND = "redirect_text_to_decision_ticket";
const DIRECTIVE_SOURCE_PREFIX = "directive:";
const RECOVERY_LABEL_PREFIX = "recovery:";

export type ControllerLedgerState = Pick<MissionState, "intents" | "results">;

export interface ControllerAuthoredEntry {
  /** `directive:<id>` | `recovery:<ticket>:<trigger>`. */
  label: string;
  /** Content the op wrote (H-1). */
  ticketSha256: string;
}

/** Ticket id → label + content hash of every APPLIED controller decision op that pinned one. */
export function controllerAuthoredDecisionLedger(
  state: ControllerLedgerState,
): Map<string, ControllerAuthoredEntry> {
  const ledger = new Map<string, ControllerAuthoredEntry>();
  for (const intent of Object.values(state.intents)) {
    const p = intent.params;
    const id = p.ticket_id;
    const result = state.results[intent.operation_id];
    if (typeof id !== "string" || result?.outcome !== "applied") continue;
    const ticketSha256 = result.ticket_sha256;
    if (ticketSha256 === undefined) continue;
    if (intent.operation_kind === REDIRECT_DECISION_OPERATION_KIND) {
      ledger.set(id, {
        label: `${DIRECTIVE_SOURCE_PREFIX}${String(p.directive_id)}`,
        ticketSha256,
      });
    } else if (intent.operation_kind === RECOVERY_TICKET_OP_KIND) {
      const label = `${RECOVERY_LABEL_PREFIX}${String(p.source_ticket)}:${String(p.trigger)}`;
      ledger.set(id, { label, ticketSha256 });
    }
  }
  return ledger;
}

function hasReadOnlyDecisionShape(ticket: Ticket): boolean {
  return (
    ticket.revision === 1 && // never amended since the controller wrote it (review L1)
    ticket.kind === "decision" &&
    ticket.command_adapter === "decision-v1" &&
    ticket.allowed_paths.length === 0
  );
}

/**
 * D2.6: in the applied ledger AND still in the read-only shape the controller
 * wrote — `sources` exactly the ledger's label (review G2 L1) — AND the very
 * content the op pinned (H-1).
 */
export function controllerAuthoredDecisionTicket(
  ticket: Ticket,
  ledger: Map<string, ControllerAuthoredEntry>,
): { source_label: string } | null {
  const entry = ledger.get(ticket.id);
  if (entry === undefined || !hasReadOnlyDecisionShape(ticket)) return null;
  if (ticket.sources.length !== 1 || ticket.sources[0] !== entry.label) return null;
  return ticketContentSha256(ticket) === entry.ticketSha256 ? { source_label: entry.label } : null;
}

/**
 * 3.40.0: the source ticket + trigger of a controller-authored RECOVERY
 * decision (ledger label `recovery:<source>:<trigger>`), or undefined.
 */
export function controllerRecoveryOrigin(
  ticket: Ticket,
  state: ControllerLedgerState,
): { sourceTicketId: string; trigger: RecoveryTicketTrigger } | undefined {
  const label = controllerAuthoredDecisionTicket(
    ticket,
    controllerAuthoredDecisionLedger(state),
  )?.source_label;
  if (label === undefined || !label.startsWith(RECOVERY_LABEL_PREFIX)) return undefined;
  const rest = label.slice(RECOVERY_LABEL_PREFIX.length);
  const cut = rest.lastIndexOf(":");
  const trigger = RECOVERY_TICKET_TRIGGERS.find((t) => t === rest.slice(cut + 1));
  return trigger === undefined ? undefined : { sourceTicketId: rest.slice(0, cut), trigger };
}

/**
 * 06e R3: the directive id behind a controller-made redirect decision, or
 * undefined. All of: exactly one `directive:<id>` source; the ledger operation
 * `redirect-decision-<id>` has an `applied` result and its intent names THIS
 * ticket; and the shape check above.
 */
export function controllerRedirectDecisionDirectiveId(
  ticket: Ticket,
  state: ControllerLedgerState,
): string | undefined {
  const directiveSources = ticket.sources.filter((s) => s.startsWith(DIRECTIVE_SOURCE_PREFIX));
  if (directiveSources.length !== 1) return undefined;
  const directiveId = (directiveSources[0] as string).slice(DIRECTIVE_SOURCE_PREFIX.length);
  const operationId = `redirect-decision-${directiveId}`;
  if (state.results[operationId]?.outcome !== "applied") return undefined;
  const intent = state.intents[operationId];
  if (
    intent?.operation_kind !== REDIRECT_DECISION_OPERATION_KIND ||
    intent.params.ticket_id !== ticket.id ||
    intent.params.directive_id !== directiveId
  ) {
    return undefined;
  }
  const authored = controllerAuthoredDecisionTicket(
    ticket,
    controllerAuthoredDecisionLedger(state),
  );
  return authored?.source_label === `${DIRECTIVE_SOURCE_PREFIX}${directiveId}`
    ? directiveId
    : undefined;
}
